// zr-core/linkCapacity — RAW LINK CAPACITY (fast.com-style), distinct from media throughput.
//
// The passive getStats telemetry (`quality-changed`) reports the CALL's actual RTP throughput and
// the browser's bandwidth ESTIMATE (BWE / availableOutgoingKbps). Neither is the raw link capacity
// a speed-test tool measures. To get that you must actively push/pull a payload and time it. This
// module holds the PURE maths + smoothing; the active HTTP probe lives in zr-call (needs fetch),
// and it is session-scoped (runs only while a call is active, alongside the CoTURN keep-alive ping).

/** One capacity measurement (Mbps down/up) at a point in time. */
export interface LinkCapacitySample {
  /** Download capacity, megabits/sec. */
  downMbps: number;
  /** Upload capacity, megabits/sec. */
  upMbps: number;
  /** Round-trip latency to the probe endpoint (ms), for context vs the CoTURN ping RTT. */
  latencyMs?: number;
  /** Epoch ms the sample was taken. */
  at: number;
  /** The endpoint the capacity was measured against (so the UI can label the path). */
  endpoint?: string;
}

/** No single consumer/edge link exceeds this; a higher reading means the transfer was too fast
 *  to time reliably (e.g. loopback), so we clamp rather than report a nonsensical figure. */
export const MAX_LINK_MBPS = 10_000 as const;

/** Bytes transferred over an elapsed time → megabits per second (raw capacity), clamped sanely. */
export function throughputMbps(bytes: number, elapsedMs: number): number {
  if (elapsedMs <= 0 || bytes <= 0) return 0;
  const mbps = (bytes * 8) / 1e6 / (elapsedMs / 1000);
  return Math.round(Math.min(mbps, MAX_LINK_MBPS) * 10) / 10;
}

/**
 * Tracks link capacity across repeated probes. A speed test reports the SUSTAINED PEAK (a single
 * sample can dip from congestion, TCP slow-start or the call itself competing), so this reports a
 * lightly-smoothed value that biases toward the recent max rather than a decaying average — the
 * honest "how fast is this link" figure. Pure + testable.
 */
export class LinkCapacityTracker {
  private down = 0;
  private up = 0;
  constructor(private decay = 0.7) {}

  /** Fold a fresh sample in; returns the current best-estimate down/up Mbps. */
  update(sample: { downMbps: number; upMbps: number }): { downMbps: number; upMbps: number } {
    this.down = this.blend(this.down, sample.downMbps);
    this.up = this.blend(this.up, sample.upMbps);
    return { downMbps: this.down, upMbps: this.up };
  }

  private blend(prevPeak: number, next: number): number {
    if (next <= 0) return prevPeak;
    if (prevPeak <= 0) return next;
    // Jump up immediately to a higher reading (capacity revealed); ease down slowly (ignore a
    // transient dip) — the fast.com behaviour of settling on the sustained peak.
    if (next >= prevPeak) return Math.round(next * 10) / 10;
    return Math.round((this.decay * prevPeak + (1 - this.decay) * next) * 10) / 10;
  }

  get downMbps(): number {
    return this.down;
  }
  get upMbps(): number {
    return this.up;
  }
}
